import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  AccountTree as DepartmentIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { API_BASE } from '../utils/apiUtils';

const CreateUser = () => {
  const [userData, setUserData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    userCode: '',
    organizationId: '',
    departmentId: '',
    groups: [],
    allowedDepartmentIds: [], // For ADMIN users - departments they can upload to
    active: true
  });

  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchOrganizations();
    // Auto-generate user code on component mount
    generateUserCode();
  }, []);

  useEffect(() => {
    if (userData.organizationId) {
      fetchDepartments(userData.organizationId);
      // Generate new user code when organization changes
      generateUserCode();
    } else {
      setDepartments([]);
      setUserData(prev => ({ ...prev, departmentId: '' }));
    }
  }, [userData.organizationId]);

  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const response = await fetch(`${API_BASE}/admin/users/form-data`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const organizations = data.organizations || [];
        const availableGroups = data.availableGroups || [];
        setOrganizations(Array.isArray(organizations) ? organizations : []);
        setAvailableRoles(Array.isArray(availableGroups) ? availableGroups : []);
        console.log('Available roles for current user:', availableGroups);
      } else {
        throw new Error('Failed to fetch form data');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      showNotification('Failed to load organizations', 'error');
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchDepartments = async (organizationId) => {
    setLoadingDepts(true);
    try {
      const response = await fetch(`${API_BASE}/documents/departments?organizationId=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDepartments(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to fetch departments');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      showNotification('Failed to load departments', 'error');
      setDepartments([]);
    } finally {
      setLoadingDepts(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!userData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!userData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!userData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!userData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!userData.password) {
      newErrors.password = 'Password is required';
    } else if (userData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!userData.userCode.trim()) {
      newErrors.userCode = 'User code is required';
    }

    if (!userData.organizationId) {
      newErrors.organizationId = 'Organization is required';
    }

    // Department validation based on role
    if (userData.groups.includes('ADMIN')) {
      if (!userData.allowedDepartmentIds || userData.allowedDepartmentIds.length === 0) {
        newErrors.departmentId = 'At least one department must be selected for ADMIN users';
      }
    } else {
      if (!userData.departmentId) {
        newErrors.departmentId = 'Department is required';
      }
    }

    if (userData.groups.length === 0) {
      newErrors.groups = 'At least one role must be selected';
    }
    
    // Validate that selected roles are allowed for current user
    if (userData.groups.length > 0 && availableRoles.length > 0) {
      const invalidRoles = userData.groups.filter(role => !availableRoles.includes(role));
      if (invalidRoles.length > 0) {
        newErrors.groups = `You don't have permission to assign these roles: ${invalidRoles.join(', ')}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Auto-generate user code when first name changes
    if (field === 'firstName' && value.trim()) {
      setTimeout(() => generateUserCode(), 100); // Small delay to ensure state is updated
    }
  };

  const handleRoleToggle = (role) => {
    // Check if the user has permission to assign this role
    if (!availableRoles.includes(role)) {
      showNotification(`You don't have permission to assign the ${role} role`, 'error');
      return;
    }

    setUserData(prev => {
      const newGroups = prev.groups.includes(role)
        ? prev.groups.filter(r => r !== role)
        : [...prev.groups, role];
      
      let newData = {
        ...prev,
        groups: newGroups
      };
      
      // If ADMIN role is being added, clear single departmentId
      if (role === 'ADMIN' && newGroups.includes('ADMIN')) {
        newData.departmentId = '';
      }
      
      // If ADMIN role is being removed, clear department permissions
      if (role === 'ADMIN' && !newGroups.includes('ADMIN')) {
        newData.allowedDepartmentIds = [];
      }
      
      return newData;
    });

    // Clear role and department errors
    if (errors.groups) {
      setErrors(prev => ({ ...prev, groups: '' }));
    }
    if (errors.departmentId) {
      setErrors(prev => ({ ...prev, departmentId: '' }));
    }
  };

  const handleDepartmentPermissionToggle = (departmentId) => {
    setUserData(prev => ({
      ...prev,
      allowedDepartmentIds: prev.allowedDepartmentIds.includes(departmentId)
        ? prev.allowedDepartmentIds.filter(id => id !== departmentId)
        : [...prev.allowedDepartmentIds, departmentId]
    }));
  };

  const generateUserCode = () => {
    // Generate user code based on first name
    let userCode = '';
    
    if (userData.firstName.trim()) {
      const firstName = userData.firstName.trim().toLowerCase();
      // Take first 4 characters of first name, pad with zeros if needed
      const namePart = firstName.substring(0, 4).padEnd(4, '0');
      // Generate a random 3-digit number
      const numberPart = Math.floor(Math.random() * 900) + 100; // 100-999
      userCode = `${namePart}${numberPart}`;
    } else {
      // Fallback if no first name yet
      const random = Math.floor(Math.random() * 900) + 100;
      userCode = `USER${random}`;
    }
    
    setUserData(prev => ({ ...prev, userCode }));
    
    // Clear user code error if exists
    if (errors.userCode) {
      setErrors(prev => ({ ...prev, userCode: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('Please fix the errors below', 'error');
      return;
    }

    setLoading(true);
    try {
      // Prepare the payload with proper data types
      const payload = {
        ...userData,
        organizationId: userData.organizationId ? parseInt(userData.organizationId) : null,
        departmentId: userData.departmentId ? parseInt(userData.departmentId) : null,
        groups: userData.groups // Backend expects Set<String> but JSON sends as array which Spring converts
      };

      const response = await fetch(`${API_BASE}/admin/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showNotification('User created successfully!', 'success');
        
        // Reset form
        setUserData({
          username: '',
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          userCode: '',
          organizationId: '',
          departmentId: '',
          groups: [],
          allowedDepartmentIds: [],
          active: true
        });
        setErrors({});
      } else {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification(error.message || 'Failed to create user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUserData({
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      userCode: '',
      organizationId: '',
      departmentId: '',
      groups: [],
      allowedDepartmentIds: [],
      active: true
    });
    setErrors({});
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="form-icon">
          <PersonAddIcon />
        </div>
        <h1 className="form-title">Create New User</h1>
        <p className="form-subtitle">
          Add a new user to your organization with the appropriate roles and permissions
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal Information Section */}
        <div className="form-section">
          <h2 className="form-section-title">
            <PersonAddIcon className="form-section-icon" />
            Personal Information
          </h2>
          
          <div className="form-group">
            <label className="form-label form-label-required">Username</label>
            <input
              type="text"
              className={`form-input ${errors.username ? 'form-input-error' : ''}`}
              placeholder="Enter username"
              value={userData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
            />
            {errors.username && <span className="form-error">{errors.username}</span>}
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label className="form-label form-label-required">First Name</label>
              <input
                type="text"
                className={`form-input ${errors.firstName ? 'form-input-error' : ''}`}
                placeholder="Enter first name"
                value={userData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
              {errors.firstName && <span className="form-error">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label className="form-label form-label-required">Last Name</label>
              <input
                type="text"
                className={`form-input ${errors.lastName ? 'form-input-error' : ''}`}
                placeholder="Enter last name"
                value={userData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
              {errors.lastName && <span className="form-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Email Address</label>
            <input
              type="email"
              className={`form-input ${errors.email ? 'form-input-error' : ''}`}
              placeholder="user@company.com"
              value={userData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Password</label>
            <input
              type="password"
              className={`form-input ${errors.password ? 'form-input-error' : ''}`}
              placeholder="Enter secure password"
              value={userData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
            />
            {errors.password && <span className="form-error">{errors.password}</span>}
            <span className="form-help">Password must be at least 6 characters long</span>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">User Code</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className={`form-input ${errors.userCode ? 'form-input-error' : ''}`}
                placeholder="Enter user code or generate one"
                value={userData.userCode}
                onChange={(e) => handleInputChange('userCode', e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={generateUserCode}
                style={{ 
                  padding: '8px 16px',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                <RefreshIcon style={{ width: '16px', height: '16px' }} />
                Generate
              </button>
            </div>
            {errors.userCode && <span className="form-error">{errors.userCode}</span>}
            <span className="form-help">Unique identifier based on first name (e.g., soem001, john002). Auto-generates when you type the first name.</span>
          </div>
        </div>

        {/* Organization & Department Section */}
        <div className="form-section">
          <h2 className="form-section-title">
            <BusinessIcon className="form-section-icon" />
            Organization & Department
          </h2>
          
          <div className="form-group-row">
            <div className="form-group">
              <label className="form-label form-label-required">Organization</label>
              <div style={{ position: 'relative' }}>
                <select
                  className={`form-select ${errors.organizationId ? 'form-input-error' : ''}`}
                  value={userData.organizationId}
                  onChange={(e) => handleInputChange('organizationId', e.target.value)}
                  disabled={loadingOrgs}
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {loadingOrgs && (
                  <div style={{ position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }}>
                    <CircularProgress size={16} />
                  </div>
                )}
              </div>
              {errors.organizationId && <span className="form-error">{errors.organizationId}</span>}
            </div>

            <div className="form-group">
              <label className="form-label form-label-required">
                {userData.groups.includes('ADMIN') ? 'Departments (Select Multiple)' : 'Department'}
              </label>
              
              {/* Multi-select checkboxes for ADMIN users */}
              {userData.groups.includes('ADMIN') ? (
                <div style={{ 
                  border: '1px solid var(--color-border-strong)', 
                  borderRadius: '4px', 
                  padding: '10px',
                  minHeight: '45px',
                  backgroundColor: loadingDepts || !userData.organizationId ? 'var(--color-bg-tertiary)' : 'white'
                }}>
                  {loadingDepts ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={16} style={{ marginRight: '8px' }} />
                      Loading departments...
                    </div>
                  ) : !userData.organizationId ? (
                    <span style={{ color: 'var(--color-text-secondary)' }}>Select organization first</span>
                  ) : departments.length === 0 ? (
                    <span style={{ color: 'var(--color-text-secondary)' }}>No departments available</span>
                  ) : (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '8px'
                    }}>
                      {departments.map((dept) => (
                        <label key={dept.id} className="form-checkbox" style={{ margin: '0', fontSize: '14px' }}>
                          <input
                            type="checkbox"
                            checked={userData.allowedDepartmentIds.includes(dept.id)}
                            onChange={() => handleDepartmentPermissionToggle(dept.id)}
                            disabled={loadingDepts || !userData.organizationId}
                          />
                          {dept.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Single select dropdown for non-ADMIN users */
                <div style={{ position: 'relative' }}>
                  <select
                    className={`form-select ${errors.departmentId ? 'form-input-error' : ''}`}
                    value={userData.departmentId}
                    onChange={(e) => handleInputChange('departmentId', e.target.value)}
                    disabled={loadingDepts || !userData.organizationId}
                  >
                    <option value="">
                      {!userData.organizationId ? 'Select organization first' : 'Select department'}
                    </option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {loadingDepts && (
                    <div style={{ position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }}>
                      <CircularProgress size={16} />
                    </div>
                  )}
                </div>
              )}
              
              {errors.departmentId && <span className="form-error">{errors.departmentId}</span>}
              <span className="form-help">
                {userData.groups.includes('ADMIN') 
                  ? 'Select departments this ADMIN user can upload documents to' 
                  : 'Primary department for this user'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Roles & Permissions Section */}
        <div className="form-section">
          <h2 className="form-section-title">
            <SecurityIcon className="form-section-icon" />
            Roles & Permissions
          </h2>
          
          <div className="form-group">
            <label className="form-label form-label-required">User Roles</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {availableRoles.length > 0 ? (
                availableRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleRoleToggle(role)}
                    className={`btn ${userData.groups.includes(role) ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ 
                      padding: '6px 16px',
                      fontSize: '14px',
                      borderRadius: '20px',
                      minWidth: 'auto'
                    }}
                  >
                    {role}
                    {userData.groups.includes(role) && (
                      <CheckIcon style={{ marginLeft: '4px', width: '16px', height: '16px' }} />
                    )}
                  </button>
                ))
              ) : (
                <div style={{ padding: '8px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Loading available roles...
                </div>
              )}
            </div>
            {errors.groups && <span className="form-error">{errors.groups}</span>}
            <span className="form-help">Select one or more roles for this user</span>
          </div>


          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={userData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
              />
              Account is active
            </label>
            <span className="form-help">Inactive users cannot log in to the system</span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="form-button form-button-secondary"
            onClick={resetForm}
            disabled={loading}
          >
            <RefreshIcon style={{ width: '16px', height: '16px' }} />
            Reset Form
          </button>
          
          <button
            type="submit"
            className="form-button form-button-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Creating...
              </>
            ) : (
              <>
                <PersonAddIcon style={{ width: '16px', height: '16px' }} />
                Create User
              </>
            )}
          </button>
        </div>
      </form>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          icon={notification.severity === 'success' ? <CheckIcon /> : <ErrorIcon />}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateUser;